import { makeAutoObservable } from 'mobx';
import { fromPromise, type IPromiseBasedObservable } from 'mobx-utils';
import { S3Url } from 's3u';
import type PulseApi from '../../../api/PulseApi';
import type AuthStore from '../../login/store/AuthStore';
import S3Files from '../edit/files/store/S3Files';
import type VersionFile from '../edit/files/store/VersionFile';
import Product, {
  type ProductConstructorDto,
  type ProductDto,
} from './Product';
import Products from './Products';
import type Sellable from './sellable/Sellable';
import { isUrlPointsToFile } from './utils';

export default class ProductStore {
  constructor(
    private readonly api: PulseApi,
    private readonly authStore: AuthStore,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  createProduct(initialFields: ProductDto = {}): Product {
    return this.unSerialize({ ...initialFields, savingAction: 'create' });
  }

  async duplicateProduct(productId: string): Promise<Product> {
    return this.unSerialize(
      await this.api.products.duplicateProduct(productId),
    );
  }

  fetchProduct(productId: string): IPromiseBasedObservable<Product> {
    return fromPromise(
      this.api.products
        .getProduct(productId)
        .then((dto) => this.unSerialize(dto)),
    );
  }

  fetchAllProducts(
    persistentFilter = false,
  ): IPromiseBasedObservable<Products> {
    return fromPromise(
      this.api.products.getAllProducts().then((dtos) => {
        return new Products(dtos, this.saveProduct, persistentFilter);
      }),
    );
  }

  async saveProduct(product: Product) {
    if (product.savingAction === 'archive') {
      await this.api.products.archiveProduct(product.id);
      product.setSavingAction('update');
      return;
    }

    if (product.savingAction === 'create') {
      const dto = await this.api.products.createProduct(product.toJSON());
      product.updateAfterCreation(dto);
      return;
    }

    await this.api.products.updateProduct(product.toJSON());
  }

  makeShareableLinks(
    sellable: Sellable,
    expirationDate: Date,
    enableCdn?: boolean,
  ): IPromiseBasedObservable<string[]> {
    return fromPromise(
      this.api.products.makeShareableLinks(
        sellable.id,
        expirationDate.toISOString(),
        enableCdn,
      ),
    );
  }

  fetchFileList(url: string): IPromiseBasedObservable<S3Files> {
    let actualUrl = url;

    try {
      const s3Url = new S3Url(url);
      const isFile = isUrlPointsToFile(url);

      if (isFile) {
        s3Url.setKey(`${s3Url.dirPath}/`);
        actualUrl = s3Url.href;
      }
    } catch {
      // Use empty url instead
    }

    return fromPromise(
      this.api.utils.listS3Path(actualUrl).then((dto) => {
        return new S3Files({ ...dto, originalUrl: url });
      }),
    );
  }

  checkFileExistence(file: VersionFile) {
    this.api.utils
      .listS3Path(file.url)
      .then((dto) => {
        file.setExistenceState(dto.exists ? 'exists' : 'not-exists');
      })
      .catch((e) => {
        file.setExistenceState('not-exists', e.message);
      });
  }

  private async uploadFile(file: File): Promise<string> {
    return this.api.utils.uploadFile(file);
  }

  private unSerialize(dto: ProductConstructorDto): Product {
    const forceEditMode = this.authStore.userHasRole('ADMIN', { mode: 'main' });

    return new Product(
      { ...dto, forceEditMode },
      this.saveProduct,
      this.uploadFile,
    );
  }
}
